import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SearchPanel } from "./components/SearchPanel";
import { PageWidth } from "./components/travel-ui";
import { useMarketplaceSearch } from "./context/SearchContext";
import {
  cheapestFare,
  cityMatches,
  MOCK_FLIGHTS,
  type Cabin,
} from "./data/mock-flights";
import { formatMoney, formatLongDate } from "./lib/format";
import { quoteFlight } from "./lib/pricing";
import { useWallet } from "./services/wallet.service";

type SortKey = "cheapest" | "fastest" | "earliest";

export default function MemberFlights() {
  const navigate = useNavigate();
  const { search, setSearch } = useMarketplaceSearch();
  const wallet = useWallet();

  useEffect(() => {
    setSearch({ product: "flights" });
    // Mount-only: keep the search bar on flights while this page is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sort, setSort] = useState<SortKey>("cheapest");
  const [directOnly, setDirectOnly] = useState(false);

  const rows = useMemo(() => {
    const matched = MOCK_FLIGHTS.filter((flight) => {
      if (!cityMatches(search.origin, flight.fromCity, flight.fromCode)) return false;
      if (!cityMatches(search.flightTo, flight.toCity, flight.toCode)) return false;
      if (directOnly && flight.stops > 0) return false;
      return true;
    }).map((flight) => {
      const fare = cheapestFare(flight, search.cabin);
      const quote = quoteFlight(fare, search.passengers);
      return { flight, fare, quote };
    });
    matched.sort((a, b) => {
      if (sort === "fastest") return a.flight.durationMins - b.flight.durationMins;
      if (sort === "earliest") return a.flight.depart.localeCompare(b.flight.depart);
      return a.quote.total - b.quote.total;
    });
    return matched;
  }, [search.origin, search.flightTo, search.cabin, search.passengers, sort, directOnly]);

  const routeLabel =
    search.origin.trim() || search.flightTo.trim()
      ? `${search.origin.trim() || "Anywhere"} to ${search.flightTo.trim() || "anywhere"}`
      : "All routes";

  return (
    <div>
      <div className="border-b border-[#e7e7e7] bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <SearchPanel
            onSearch={(product) => {
              setSearch({ product });
              navigate(product === "flights" ? "/member/flights" : "/member/hotels");
            }}
          />
        </div>
      </div>
      <PageWidth>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{routeLabel}</h1>
            <p className="text-sm text-[#595959]">
              {formatLongDate(search.departDate)}
              {search.tripType === "round"
                ? ` – ${formatLongDate(search.returnDate)}`
                : " · One way"}
              {" · "}
              {search.passengers} passenger{search.passengers === 1 ? "" : "s"} ·{" "}
              {search.cabin}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 rounded-md border border-[#e7e7e7] bg-white px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={directOnly}
                onChange={(event) => setDirectOnly(event.target.checked)}
              />
              Direct only
            </label>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              className="rounded-md border border-[#e7e7e7] bg-white px-3 py-2 text-sm"
              aria-label="Sort flights"
            >
              <option value="cheapest">Cheapest</option>
              <option value="fastest">Fastest</option>
              <option value="earliest">Earliest departure</option>
            </select>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="rounded-lg border border-[#e7e7e7] bg-white p-8 text-center">
            <p className="font-semibold">No flights match that search.</p>
            <button
              type="button"
              className="mt-3 text-sm font-semibold text-[#006ce4]"
              onClick={() => {
                setSearch({ origin: "", flightTo: "", cabin: "Economy" as Cabin });
                setDirectOnly(false);
              }}
            >
              Show every flight
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map(({ flight, fare, quote }) => (
              <article
                key={flight.id}
                className="grid gap-4 rounded-lg border border-[#e7e7e7] bg-white p-4 md:grid-cols-[1fr_180px]"
              >
                <div>
                  <p className="text-sm text-[#595959]">
                    {flight.airline} · {flight.number} · {flight.aircraft}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    <div>
                      <p className="text-2xl font-bold">{flight.depart}</p>
                      <p className="text-sm font-semibold">{flight.fromCode}</p>
                      <p className="text-xs text-[#595959]">{flight.fromCity}</p>
                    </div>
                    <div className="min-w-24 text-center text-xs text-[#595959]">
                      <p>{flight.duration}</p>
                      <p className="my-1 border-t border-[#e7e7e7]" />
                      <p>{flight.stops === 0 ? "Direct" : `${flight.stops} stop`}</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{flight.arrive}</p>
                      <p className="text-sm font-semibold">{flight.toCode}</p>
                      <p className="text-xs text-[#595959]">{flight.toCity}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-[#008009]">
                    {fare.name} · {fare.cabin} · {fare.baggage}
                    {fare.refundable ? " · Refundable" : ""}
                  </p>
                </div>
                <div className="flex flex-col items-start justify-between md:items-end">
                  <div className="md:text-right">
                    <p className="text-xs font-semibold text-[#008009]">Member fare</p>
                    <p className="text-2xl font-bold">{formatMoney(quote.total, wallet.currency)}</p>
                    <p className="text-xs text-[#595959]">
                      {search.passengers} traveller
                      {search.passengers === 1 ? "" : "s"} · taxes included
                    </p>
                  </div>
                  <Link
                    to={`/member/flights/${flight.id}`}
                    className="mt-3 inline-flex h-10 items-center rounded-md bg-[#006ce4] px-4 text-sm font-semibold text-white hover:bg-[#0057b8]"
                  >
                    Select
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </PageWidth>
    </div>
  );
}
